<template>
  <div id="mealPlans">
    <vue-loading :active="isLoading" :is-full-page="true" />
    <div id="mealPlan" v-if="mealPlan" class="d-flex gap10 flex-column">
      <div v-for="group in orderedGroups" :key="group.description" class="meal-container">
        <section
          class="meal-plan-group-section"
          :aria-labelledby="group.description ? `meal-group-${group.id}-title` : null"
          :aria-label="!group.description ? `meal-group-${group.id}` : null"
        >
          <div class="meal-plan-group-header">
            <h3
              v-if="group.description"
              :id="`meal-group-${group.id}-title`"
              class="meal-plan-group-title"
            >
              {{ group.description }}
            </h3>
            <div class="meal-plan-group-header-right meal-group">
              <span class="text-right">
                <strong>{{ productCountByGroup(group.id) }}</strong> / <strong>{{ group.maxProducts }}</strong>
                <span v-if="group.minProducts > 0" class="mealplan-group-requirement"> ({{ $t('mealPlan.label.min') }}: {{ group.minProducts }})</span>
              </span>
            </div>
          </div>
          <div class="meal-plan-group-body">
            <div class="grid-layout">
              <div
                v-for="product in group.items"
                :key="product?.id"
                :class="{ 'disabled-item': !isMealPlanValid || product.locked }"
              >
                <ProductContainer
                  :product="product"
                  :basket-items="getBasketItemsByProdId(product.id)"
                  :group-id="group.id"
                  :group-max="group.maxProducts"
                  :group-count="getGroupCount(group.id)"
                  @update-product="onProductUpdate"
                  @edit-product="onProductEdit"
                  @view-details="onViewProductDetails"
                />
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- Modal for product details -->
      <b-modal id="product-details-modal" hide-footer :title="selectedProduct ? selectedProduct.name : ''">
        <ProductDetails
          v-if="selectedProduct && selectedProduct.groupId"
          :productId="selectedProduct.productId"
          :max-quantity="getGroupRemainingCount(selectedProduct.groupId)"
          :groupId="selectedProduct.groupId"
          @close="closeProductDetailsModal"
          @product-update="handleProductUpdateWithOptions"
        />
      </b-modal>

      <!-- Modal for basket products -->
      <b-modal id="basket-product-editor-modal" hide-footer :title="selectedProduct ? selectedProduct.name : ''">
        <BasketProductEditor v-if="selectedProduct && selectedProduct.groupId"
           :basket-items="getBasketItemsByProdId(selectedProduct.productId)"
           :meal-plan-groups="mealPlan?.groups"
           :product-max-map="productMaxMap"
           @close="closeBasketProductEditorModal"
           @product-quantity-change="onProductQuantityChange"
        />
      </b-modal>
    </div>
  </div>
</template>

<script>
import VueLoading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import ProductDetails from "@/components/sub-components/Product/ProductDetails.vue";
import {basketService} from "@/services/api/basketService";
import {OrderTypes} from "@/constants/constants";
import {useAppStore} from "@/stores/app";
import {useShopStore} from "@/stores/shop";
import {encode64} from "@/utils/encodeUtils";
import { isMealPlanValid as isMealPlanValidUtil, getGroupProductCounts } from "@/utils/mealPlanUtils";
import BasketProductEditor from "@/components/sub-components/Basket/BasketProductEditor.vue";
import productDetailsMixin from "@/components/mixins/productDetailsMixin";
import ProductContainer from "@/components/data-display/ProductContainer.vue";
import { getProductCountInGroup } from "@/utils/productCountUtils";

export default {
  name: "MealPlan",
  mixins: [productDetailsMixin],
  components: {
    ProductContainer: ProductContainer,
    BasketProductEditor,
    ProductDetails,
    VueLoading,
  },
  props: {
    mealPlan: {
      type: Object,
      required: true,
    },
    basketTile: {
      type: Object,
      required: false,
      default: null
    },
    groupProductCount: {
      type: Object,
      required: false,
      default: () => ({})
    },
    productMaxMap: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data() {
    return {
      selectedProduct: {},
      selectedProductsCount: {}, // { [groupId]: { [productId]: quantity } }
      isLoading: false,
    };
  },
  computed: {
    isMealPlanValid() {
      return isMealPlanValidUtil(this.mealPlan);
    },
    /** @file public/settings/config/default.js */
    config() {
      return window.config;
    },
    orderedGroups() {
      // Return groups ordered by groupOrder (ensure numeric sort)
      return (this.mealPlan?.groups || []).slice().sort((a, b) => Number(a.groupOrder || 0) - Number(b.groupOrder || 0));
    }
  },
  setup() {
    const appStore = useAppStore();
    const shopStore =useShopStore()

    return {
      appStore,
      shopStore
    };
  },
  watch: {
    basketTile: {
      immediate: true,
      handler(newBasketTile) {
        const counts = {};
        if (newBasketTile && Array.isArray(newBasketTile.basketItems)) {
          newBasketTile.basketItems.forEach(item => {
            let groupId = null;
            try {
              const ext = typeof item.extendedInfos === 'string' ? JSON.parse(item.extendedInfos) : item.extendedInfos;
              groupId = ext?.mealPlan?.groupId;
            } catch {
              groupId = item.groupId;
            }
            if (!groupId) return;
            if (!counts[groupId]) counts[groupId] = {};
            counts[groupId][item.prodId] = getProductCountInGroup(newBasketTile.basketItems, groupId, item.prodId);
          });
        }
        this.selectedProductsCount = counts;
      }
    }
  },
  methods: {
    getUnavailableArticleMessage(productName) {
      return this.$t('notification.itemUnavailable', [productName || this.$t('system.label.noData')]);
    },
    getProductNameById(productId) {
      const product = (this.orderedGroups || [])
        .flatMap(group => group.items || [])
        .find(item => Number(item?.id) === Number(productId));

      return product?.name || product?.description || null;
    },
    async handleProductUpdateWithOptions(productId, quantity, price, productOptions, groupId) {
      await this.handleProductUpdate(productId, false, false, '', 'increment', groupId, productOptions, quantity);
    },
    async handleProductUpdate(productId, isLocked, hasChildren, name, type, groupId, productOptions, quantity = 1) {
      if (hasChildren) {
        if (type === 'increment') {
          this.viewProductDetails(productId, isLocked, name, groupId);
        }
      } else {
        if (type === 'increment') {
          await this.addToBasket(productId, quantity, productOptions, groupId);
        } else if (type === 'decrement') {
          await this.removeFromBasket(productId, quantity, groupId);
        }
      }
    },
    productCountByGroup(groupId) {
      return getGroupProductCounts(this.basketTile?.basketItems)[groupId] || 0;
    },
    /**
     *
     * @param productId
     * @param quantity
     * @param {string[]} productOptions format: `["key=value","key1=value1","keyN=valueN"]`
     * @param groupId
     * */
    async addToBasket(productId, quantity, productOptions, groupId) {
      this.isLoading = true;
      try {
        let result, options = null;
        if (Array.isArray(productOptions) && productOptions.length > 0) {
          // Defensive: flatten deeply nested arrays and filter only strings
          const flatOptions = [].concat(...productOptions).filter(opt => typeof opt === 'string');
          options = flatOptions.reduce((acc, option) => {
            const [key, value] = option.split('=');
            acc[key] = value;
            return acc;
          }, { prod: productId });
          options.kkjson = encode64(JSON.stringify(options));
        }

        const mealPlan = {
          mealPlanId: this.mealPlan.id,
          reservationDateTime: this.mealPlan.reservationDateTime,
          groupId: groupId
        };

        if (options) {
          result = await this.addComplexProductToBasket(productId, quantity, options, mealPlan);
        } else {
          result = await this.addProductToBasket(productId, quantity, mealPlan);
        }
        if (result && result.exception) {
          const productName = this.getProductNameById(productId) || this.selectedProduct?.name;

          if (result.exception.code === 64) {
            this.makeToast('danger', this.$t('mealPlan.messages.error.addItemMinError'));
          } else if (result.exception.code === 65) {
            this.makeToast('danger', this.$t('mealPlan.messages.error..addItemMaxError'));
          } else if (result.exception.code === 66) {
            this.makeToast('danger', this.getUnavailableArticleMessage(productName));
          } else {
            this.makeToast('danger', this.$t('mealPlan.messages.error.addItemFailed'));
          }
          return;
        }
        // Emit updated basketTile to parent, watcher will update counts and basket total
        if (result) this.$emit('basket-tile-updated', result.basketTile);
      } catch (error) {
        console.error("Error adding product to basket:", error);
        this.makeToast('danger', this.$t('mealPlan.messages.error.addItemFailed'));
      } finally {
        this.isLoading = false;
      }
    },
    async addComplexProductToBasket(productId, quantity, options, mealPlan) {
      this.isLoading = true;
      try {
        const result = await basketService.addComplexProductToBasket(productId, OrderTypes.DINE_IN, quantity, options, mealPlan);
        return result;
      } catch (error) {
        this.makeToast('danger', this.$t('mealPlan.messages.error.addItemFailed'));
        return null;
      } finally {
        this.isLoading = false;
      }
    },
    async addProductToBasket(productId, quantity, mealPlan) {
      this.isLoading = true;
      try {
        const result = await basketService.addProductToBasket(productId, OrderTypes.DINE_IN, quantity, mealPlan);
        return result;
      } catch (error) {
        this.makeToast('danger', this.$t('mealPlan.messages.error.addItemFailed'));
        return null;
      } finally {
        this.isLoading = false;
      }
    },
    async removeFromBasket(productId, quantity, groupId) {
      this.isLoading = true;
      try {
        const mealPlan = {
          mealPlanId: this.mealPlan.id,
          reservationDateTime: this.mealPlan.reservationDateTime,
          groupId: groupId
        };
        const result = await basketService.removeProductFromBasketByProductId(productId, quantity, OrderTypes.DINE_IN, mealPlan);
        if (result && result.exception) {
          const errorCode = result.exception.code;
          const errorMessages = {
            64: 'mealPlan.messages.error.removeItemMinError',
            65: 'mealPlan.messages.error.removeItemMaxError',
          };
          this.makeToast('danger', this.$t(errorMessages[errorCode] || 'mealPlan.messages.error.removeItemFailed'));
          return;
        }
        if (result) this.$emit('basket-tile-updated', result.basketTile);
      } catch (error) {
        console.error("Error removing product from basket:", error);
        this.makeToast('danger', this.$t('mealPlan.messages.error.removeItemFailed'));
      } finally {
        this.isLoading = false;
      }
    },
    // Get the current count of a product within a group
    getMaxAvailableForProduct(groupId, productId) {
      // Calculate the group max
      const group = this.mealPlan?.groups?.find(g => g.id === groupId);
      if (!group) return 0;
      const max = group.maxProducts || 0;
      // Calculate the total selected in this group (all products)
      const selectedInGroup = Object.values(this.selectedProductsCount[groupId] || {}).reduce((sum, val) => sum + val, 0);
      // Calculate the current selected for this product
      const currentSelected = this.selectedProductsCount[groupId]?.[productId] || 0;
      // The max you can set for this product is: max - (selectedInGroup - currentSelected)
      return Math.max(0, max - (selectedInGroup - currentSelected));
    },
    viewProductDetails(productId, isLocked, name, groupId) {
      if (this.isMealPlanValid && !isLocked) {
        this.selectedProduct = { productId, name, groupId };
        this.$bvModal.show("product-details-modal");
      }
    },
    onMealItemClick(event, product, group) {
      // Prevent if click originated from Button or SpinButton or their children
      const path = event.composedPath ? event.composedPath() : (event.path || []);
      if (
        path.some(
          el =>
            el?.classList?.contains('p-2') || // Button
            el?.classList?.contains('spin-button') // Add a class to SpinButton root if not present
        )
      ) {
        return;
      }
      this.viewProductDetails(product.id, product.isLocked, product.name, group.id);
    },
    getBasketItemsByProdId(prodId) {
      return this.basketTile?.basketItems?.filter(item => item.prodId === prodId) || [];
    },
    openBasketProductEditor(productId, name, groupId, isLocked) {
      if (this.isMealPlanValid && !isLocked) {
        this.selectedProduct = { productId, name, groupId };
        this.$bvModal.show("basket-product-editor-modal");
      }
    },
    closeBasketProductEditorModal() {
      this.$bvModal.hide("basket-product-editor-modal");
    },
    onProductQuantityChange(basketId, newQuantity) {
      this.$emit('product-quantity-change', basketId, newQuantity);
    },
    onProductUpdate(payload) {
      const { productId, isLocked, hasChildren, productName, type, value, groupId } = payload || {};
      this.handleProductUpdate(productId, isLocked, hasChildren, productName, type, groupId, undefined, value || 1);
    },
    onProductEdit(payload) {
      const { productId, productName, isLocked, groupId } = payload || {};
      this.openBasketProductEditor(productId, productName, groupId, isLocked);
    },
    onViewProductDetails(payload) {
      const { productId, isLocked, productName, groupId } = payload || {};
      this.viewProductDetails(productId, isLocked, productName, groupId);
    },
    getGroupRemainingCount(groupId) {
      // Use the utility to get remaining count for a group
      const { getGroupRemainingCounts } = require('@/utils/mealPlanUtils');
      return getGroupRemainingCounts(this.basketTile?.basketItems, this.mealPlan?.groups)[groupId] || 0;
    },
    getGroupCount(groupId) {
      return (this.basketTile?.basketItems || [])
        .filter(item => {
          let itemGroupId = null;
          try {
            const ext = typeof item.extendedInfos === 'string' ? JSON.parse(item.extendedInfos) : item.extendedInfos;
            itemGroupId = ext?.mealPlan?.groupId;
          } catch {
            itemGroupId = item.groupId;
          }
          return itemGroupId === groupId;
        })
        .reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    },
  },
};
</script>

<style scoped lang="scss">
@import '@/css/customizer';

/* Style updates for disabled state */
.button.disabled {
  background-color: #d3d3d3; /* Light gray to indicate disabled */
  pointer-events: none;
}

.disabled-item {
  opacity: 0.5;
  pointer-events: none;
}

.meal-container {
  margin-top: 4px
}

.meal-plan-group-section {
  margin-bottom: 1.5rem;
}

.meal-plan-group-header {
  display: flex;
  position: relative;
  align-items: center;
  gap: 1rem;
  padding: var(--layout-padding, 18px);
}

.meal-plan-group-title {
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  text-transform: var(--heading-text-transform, none);
}

.meal-plan-group-header-right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
  text-align: right;
}

.meal-plan-group-body {
  text-align: left;
}

.meal-group {
  padding: 0 10px;
}

.mealplan-group-requirement {
  color: custom-chain(mealplan-group-color-requirement,red);
}

.text-left {
  flex: 1; /* Allow the left text to grow */
  text-align: left; /* Align left text */
}

.text-right {
  text-align: right; /* Align right text */
}

.meal-item {
  display: flex;
  flex: 1 0 360px;
  gap: 10px;
  text-align: left;
  align-items: stretch;
  transition: background-color 0.3s ease, transform 0.3s ease; /* Smooth transition effect */
  height: 100%;
}

.meal-item:hover {
  background-color: #e0e0e0;
  transform: scale(1.02);
  cursor: pointer;
}

.meal-image {
  width: 120px;
  height: auto;
  object-fit: cover;
}

.meal-details {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.meal-description {
  font-weight: bold;
  text-align: left;
}

.properties {
  filter: hue-rotate(45deg);
}

.allergens, .properties {
  display: flex;
  flex-wrap: wrap;
  gap: 2px;
  margin-top: 5px;
}

.allergen-img,
.property-img {
  max-width: 30px;
}

.meal-price {
  font-size: 14px;
  align-self: center;
  flex-grow: 1;
  text-align: right;
}

.button {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  transition: background-color 0.3s;
}

.button:hover,
.button:focus {
  background-color: #0056b3;
}

.button:active {
  background-color: #004494;
}
</style>
